import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
// UserModal.tsx — Co Marca — Crear Usuario
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, User, Mail, Lock, ShieldCheck } from 'lucide-react';
import { registerUser } from '../../services/userService';
import { fetchUserLevels } from '../../services/userLevelService';

interface UserLevel { id: number; nombre: string; activo: boolean; }
interface UserModalProps {
    isOpen: boolean; onClose: () => void;
    isActive: boolean; toggleActive: () => void;
    onUserCreated: (user: any) => void;
}

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark:     '#ea8406',
};

const css = `

.um-modal * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes umFadeIn  { from{opacity:0;} to{opacity:1;} }
@keyframes umSlideUp { from{opacity:0;transform:translateY(24px) scale(.98);} to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes spin { to{transform:rotate(360deg);} }
.um-overlay { animation:umFadeIn .22s ease both; }
.um-dialog  { animation:umSlideUp .32s cubic-bezier(.16,1,.3,1) both; }
.um-input {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid var(--ad-line, #e5e7eb); background:#fafafa;
    font-size:.88rem; color:var(--ad-ink, #374151); outline:none; transition:all .2s;
}
.um-input:focus { border-color:${colors.primary}; box-shadow:0 0 0 3px color-mix(in srgb, ${colors.primary} 7.06%, transparent); background:var(--ad-surface, #fff); }
.um-select { appearance:none; }
.um-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:10px 22px; font-size:.88rem; font-weight:800;
    cursor:pointer; box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.um-btn-primary:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.34); }
.um-btn-primary:disabled { background:var(--ad-line, #e2e8f0); color:var(--ad-muted, #94a3b8); box-shadow:none; cursor:not-allowed; }
.um-btn-ghost {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-soft, #f8fafc); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:10px 18px; font-size:.88rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.um-btn-ghost:hover { background:var(--ad-line, #f1f5f9); }
.custom-scroll::-webkit-scrollbar { width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background:rgba(97, 37, 123,.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background:rgba(97, 37, 123,.5); }
`;

const UserModal: React.FC<UserModalProps> = ({ isOpen, onClose, isActive, toggleActive, onUserCreated }) => {
    const [firstName, setFirstName]     = useState('');
    const [lastName, setLastName]       = useState('');
    const [email, setEmail]             = useState('');
    const [password, setPassword]       = useState('');
    const [selectedLevel, setSelectedLevel] = useState<number|null>(null);
    const [userLevels, setUserLevels]   = useState<UserLevel[]>([]);
    const [loading, setLoading]         = useState(false);
    const [levelsLoading, setLevelsLoading] = useState(true);
    const [error, setError]             = useState<string|null>(null);

    useEffect(() => {
        if (isOpen) {
            setFirstName(''); setLastName(''); setEmail(''); setPassword(''); setSelectedLevel(null); setError(null);
            setLevelsLoading(true);
            fetchUserLevels().then(setUserLevels).catch((() => undefined)).finally(()=>setLevelsLoading(false));
        }
    }, [isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true); setError(null);
        try {
            const newUser = await registerUser(email, firstName, lastName, password, isActive, selectedLevel);
            const lvl = userLevels.find(l=>l.id===selectedLevel);
            onUserCreated({ ...newUser, tipo_nivel_usuario: lvl||null });
            onClose();
        } catch (err: any) { setError(err.message); }
        finally { setLoading(false); }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="um-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="um-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>

                <LumaAccessiblePanel data-luma-dialog="true" className="um-dialog custom-scroll card custom-card vx-card" style={{ position:'relative', background:'var(--ad-surface, #fff)', borderRadius:'24px', width:'100%', maxWidth:'560px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 32px 80px rgba(0,0,0,.18)', border:'1.5px solid var(--ad-line, #f1f5f9)' }}>

                    {/* Barra top */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.secondary},${colors.accentDark},${colors.secondaryLight})`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 28px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                                <div style={{ width:'36px', height:'36px', borderRadius:'11px', background:`linear-gradient(135deg,${colors.primary},${colors.primaryDark})`, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 12px color-mix(in srgb, ${colors.primary} 18.82%, transparent)` }}>
                                    <Plus size={18} color="#fff"/>
                                </div>
                                <div>
                                    <LumaAccessibleTitle style={{ fontSize:'1.1rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)', letterSpacing:'-.02em' }}>Nuevo Usuario</LumaAccessibleTitle>
                                    <p style={{ fontSize:'.72rem', color:'var(--ad-muted, #94a3b8)', fontWeight:'500' }}>Completa los datos para crear el usuario</p>
                                </div>
                            </div>
                            <div style={{ display:'flex', gap:'4px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'32px', height:'32px', borderRadius:'10px', border:'1.5px solid var(--ad-line, #f1f5f9)', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-line, #f1f5f9)'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='var(--ad-soft, #f8fafc)'}>
                            <X size={15} color="var(--ad-muted, #64748b)"/>
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} style={{ padding:'20px 28px 28px', display:'flex', flexDirection:'column', gap:'16px' }}><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>

                        {/* Error toast */}
                        {error && (
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', background:`color-mix(in srgb, ${colors.primary} 3.14%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, borderRadius:'10px', padding:'10px 14px' }}>
                                <X size={14} color={colors.primary}/>
                                <span style={{ fontSize:'.82rem', color:'var(--ad-link, #2854c5)', fontWeight:'600' }}>{error}</span>
                            </div>
                        )}

                        {/* Nombre + Apellido */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <User size={11} color={colors.secondary}/> Nombre
                                </label>
                                <input type="text" value={firstName} onChange={e=>setFirstName(e.target.value)} placeholder="Nombre" className="um-input"/>
                            </div>
                            <div>
                                <label style={{ display:'block', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>Apellido</label>
                                <input type="text" value={lastName} onChange={e=>setLastName(e.target.value)} placeholder="Apellido" className="um-input"/>
                            </div>
                        </div>

                        {/* Email + Contraseña */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Mail size={11} color={colors.secondary}/> Email
                                </label>
                                <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="correo@ejemplo.com" className="um-input"/>
                            </div>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <Lock size={11} color={colors.secondary}/> Contraseña
                                </label>
                                {/* hidden honeypot */}
                                <input type="password" name="hidden-password" autoComplete="new-password" style={{ position:'absolute', opacity:0, height:0, width:0, overflow:'hidden' }} tabIndex={-1}/>
                                <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" className="um-input"/>
                            </div>
                        </div>

                        {/* Nivel + Estado */}
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
                            <div>
                                <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'650', color:'var(--ad-ink, #374151)', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                    <ShieldCheck size={11} color={colors.accentDark}/> Nivel de usuario
                                </label>
                                <select value={selectedLevel??''} onChange={e=>setSelectedLevel(Number(e.target.value)||null)} disabled={levelsLoading} className="um-input um-select">
                                    <option value="">{levelsLoading?'Cargando...':'Seleccionar nivel'}</option>
                                    {userLevels.filter(l=>l.activo).sort((a,b)=>a.id-b.id).map(l=>(
                                        <option key={l.id} value={l.id}>{l.nombre}</option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ background:'var(--ad-soft, #f8fafc)', border:'1.5px solid var(--ad-line, #f1f5f9)', borderRadius:'14px', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between' }} className="card custom-card vx-card">
                                <div>
                                    <p style={{ fontSize:'.82rem', fontWeight:'700', color:'var(--ad-ink, #0f172a)', marginBottom:'2px' }}>Estado del usuario</p>
                                    <p style={{ fontSize:'.7rem', color:isActive?colors.secondaryLight:'var(--ad-muted, #94a3b8)', fontWeight:'600' }}>
                                        {isActive?'● Activo':'○ Inactivo'}
                                    </p>
                                </div>
                                <div onClick={toggleActive} style={{ width:'44px', height:'24px', borderRadius:'100px', background:isActive?colors.secondary:'#d1d5db', position:'relative', cursor:'pointer', transition:'background .25s', flexShrink:0 }}>
                                    <div style={{ position:'absolute', top:'3px', left:isActive?'23px':'3px', width:'18px', height:'18px', borderRadius:'50%', background:'var(--ad-surface, #fff)', boxShadow:'0 1px 4px rgba(0,0,0,.2)', transition:'left .25s' }}/>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px', paddingTop:'4px' }}>
                            <button type="button" onClick={onClose} className="um-btn-ghost">Cancelar</button>
                            <button type="submit" disabled={loading} className="um-btn-primary btn btn-primary">
                                {loading
                                    ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'spin .7s linear infinite',display:'inline-block' }}/> Guardando...</>
                                    : <><Plus size={14}/> Crear Usuario</>
                                }
                            </button>
                        </div>
                    </form>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default UserModal;
